import { TrackedRoute } from "@amx/core-client-telemetry";
import { initializeIconsTwoUi } from "@amx/ux-twoui";
import React, { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { AcwApp } from "./AcwApp";
import { bootstrapPortalTelemetry } from "./core/telemetry/init";
import { AcwTwoUITelemetryProvider } from "./providers/ACWTwoUITelemetryProvider";
import { BridgeProvider } from "./providers/BridgeProvider/BridgeProvider";
import { BridgeContextProps } from "./providers/BridgeProvider/BridgeProvider.types";
import { InitializationProvider } from "./providers/InitializationProvider/InitializationProvider";

// Initialize telemetry libraries before anything is rendered.
/**
 * Telemetry initialization here works similarly to how it does in AMC.
 * When importing /core/telemetry/init, telemetry globals will be initialized in the window object.
 *
 *  Because of that, we have to make the following assumptions:
 *
 * - ACW will initialize its telemetry context by itself, it will not depend on any setup in AMC.
 * - If ACW is imported through module-fed in AMC, it will reinitialize the telemetry globals and overwrite
 *   AMC handlers.
 * - AMC and ACW cannot send client-side telemetry from the same page at the same time.
 * - ACW will be used to load the entire page within AMC, not for a single UX component.
 *
 * In the future, we will need to refactor core-<> telemetry packages to use isolated telemetry
 * contexts instead of sharing a single context in document.window.
 */
bootstrapPortalTelemetry();

/**
 * Props for the App component, the entry point for the ACW application.
 * @param bridgeImplementation Bridge implementation for the ACW application.
 * @param displayLanguage User-selected display language.
 */
interface AccountCheckupWizardProps {
	readonly bridgeImplementation: BridgeContextProps;
	readonly displayLanguage: string;
}

export let IsGenericVariantAEnabled: boolean;
export let IsGenericVariantBEnabled: boolean;

export const passkeyVariantAFlight = "PasskeyVarA";
export const passkeyVariantBFlight = "PasskeyVarB";

export const AccountCheckupWizard: React.FC<AccountCheckupWizardProps> = ({
	bridgeImplementation,
	displayLanguage,
}) => {
	useEffect(() => {
		initializeIconsTwoUi();
	}, []);

	IsGenericVariantAEnabled =
		bridgeImplementation.acwVariant === passkeyVariantAFlight;
	IsGenericVariantBEnabled =
		bridgeImplementation.acwVariant === passkeyVariantBFlight;
	return (
		<BridgeProvider bridgeImplementation={bridgeImplementation}>
			<AcwTwoUITelemetryProvider>
				<BrowserRouter basename="/account-checkup">
					<TrackedRoute
						area="acw-remote"
						view="account-checkup-wizard"
					>
						<InitializationProvider
							displayLanguage={displayLanguage}
						>
							<AcwApp />
						</InitializationProvider>
					</TrackedRoute>
				</BrowserRouter>
			</AcwTwoUITelemetryProvider>
		</BridgeProvider>
	);
};
